import { Bounce, toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { TOAST } from "@constants/index";

// Track active toasts to prevent duplicates
// Key: `${type}-${title}-${message}`, Value: toastId
const activeToastMap = new Map();

// Custom toast component that matches the Figma design
const CustomToast = ({ title, message, type, showViewDetail, onViewDetail }) => {
  const { t } = useTranslation();
  
  const getIconColor = () => {
    switch (type) {
      case TOAST.success:
        return "bg-green-500";
      case TOAST.error:
        return "bg-red-500";
      case TOAST.warn:
        return "bg-yellow-500";
      case TOAST.info:
        return "bg-blue-500";
      default:
        return "bg-green-500";
    }
  };

  const getIcon = () => {
    switch (type) {
      case TOAST.success:
        return (
          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        );
      case TOAST.error:
        return (
          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        );
      case TOAST.warn:
        return (
          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      case TOAST.info:
        return (
          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  const hasUrl = typeof message === "string" && /(https?:\/\/|ftp:\/\/|www\.)/i.test(message);
  const messageStyle = hasUrl
    ? {
      maxHeight: "300px",
      maxWidth: "700px",
      whiteSpace: "normal",
      wordBreak: "break-all", // force break for long URLs
      overflowWrap: "anywhere",
    }
    : {
      maxHeight: "300px",
      maxWidth: "700px",
      whiteSpace: "pre-line", // keep original behavior for normal text
    };

  return (
    <div className="p-4 bg-gray-800 rounded-lg shadow-lg border border-gray-700">
      {/* Header with Icon and Title */}
      <div className="flex items-center gap-3 mb-2">
        {/* Icon */}
        <div
          className={`flex-shrink-0 w-6 h-6 rounded-full ${getIconColor()} flex items-center justify-center`}
        >
          {getIcon()}
        </div>

        {/* Title */}
        <div className="text-white font-semibold text-base">{title}</div>
      </div>

      {(message || showViewDetail) && (
        <div className="flex items-end gap-3 ml-9">
          {/* Message */}
          {message ? (
            <div
              className="text-gray-300 text-sm overflow-y-auto overflow-x-hidden flex-1"
              style={messageStyle}
            >
              {message}
            </div>
          ) : null}

          {/* View Detail Button */}
          {showViewDetail && (
            <span
              onClick={onViewDetail}
              className="shrink-0 cursor-pointer text-sm text-blue-400 underline hover:text-blue-300"
            >
              {t("common.view-details")}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

const ToastConstant = (key, customTitle = null, customMessage = null, onViewDetail = null) => {
  const styleToast = {
    position: "top-right",
    autoClose: onViewDetail ? false : 3000,
    closeOnClick: true,
    draggable: true,
    theme: "dark",
    closeButton: false,
    transition: Bounce,
    hideProgressBar: true, // Show progress bar since we have autoClose
    icon: false, // This removes the default icon
    style: {
      width: "auto",
      minWidth: "auto",
      maxWidth: "none",
      padding: "unset"
    },
  };

  const getDefaultTitle = (type) => {
    switch (type) {
      case TOAST.success:
        return "Successfully created.";
      case TOAST.error:
        return "Error occurred.";
      case TOAST.warn:
        return "Warning.";
      case TOAST.info:
        return "Information.";
      default:
        return "Notification.";
    }
  };

  const getDefaultMessage = (type) => {
    switch (type) {
      case TOAST.success:
        return "";
      case TOAST.error:
        return "Something went wrong. Please try again.";
      case TOAST.warn:
        return "Please check your input and try again.";
      case TOAST.info:
        return "";
      default:
        return "Operation completed.";
    }
  };

  // Use custom title/message if provided, otherwise use defaults
  // If customMessage is explicitly passed (even empty string), use it. Otherwise use default.
  const title = customTitle || getDefaultTitle(key);
  const message = customMessage !== undefined && customMessage !== null ? customMessage : getDefaultMessage(key);

  // Create a unique key for this toast to check for duplicates
  const toastKey = `${key}-${title}`;
  
  // Check if a toast with the same content already exists
  // If it exists, return the existing toast ID without creating a new one
  if (activeToastMap.has(toastKey)) {
    const existingToastId = activeToastMap.get(toastKey);
    // Return existing toast ID, don't create a new toast
    return existingToastId;
  }

  const ToastContent = ({ closeToast }) => (
    <CustomToast
      title={title}
      message={message}
      type={key}
      showViewDetail={!!onViewDetail}
      onViewDetail={() => {
        onViewDetail?.();
        closeToast();
      }}
    />
  );

  // Add onClose callback to clean up the map when toast closes
  const toastOptions = {
    ...styleToast,
    onClose: () => {
      activeToastMap.delete(toastKey);
    },
  };

  let toastId;
  switch (key) {
    case TOAST.error:
      toastId = toast.error(<ToastContent />, toastOptions);
      break;
    case TOAST.success:
      toastId = toast.success(<ToastContent />, toastOptions);
      break;
    case TOAST.warn:
      toastId = toast.warn(<ToastContent />, toastOptions);
      break;
    case TOAST.info:
      toastId = toast.info(<ToastContent />, toastOptions);
      break;
    default:
      toastId = toast(<ToastContent />, toastOptions);
  }

  // Store the toast ID for this unique key
  activeToastMap.set(toastKey, toastId);
  
  return toastId;
};

// Export individual toast functions for easier use
export const showSuccessToast = (customTitle = null, customMessage = null) => {
  return ToastConstant(TOAST.success, customTitle, customMessage);
};

export const showErrorToast = (customTitle = null, customMessage = null) => {
  return ToastConstant(TOAST.error, customTitle, customMessage);
};

export const showWarningToast = (customTitle = null, customMessage = null) => {
  return ToastConstant(TOAST.warn, customTitle, customMessage);
};

export const showInfoToast = (customTitle = null, customMessage = null) => {
  return ToastConstant(TOAST.info, customTitle, customMessage);
};

export default ToastConstant;
